import { Link, Navigate, useParams } from "react-router-dom";
import { useSchoolBootstrap } from "@/hooks/use-school-bootstrap";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { isStaffRole } from "@lms/backend/lib/auth/post-login-redirect";
import { buttonClasses, PageShell } from "@/components/ui";
import { AiTutorRebuildPanel } from "@/components/instructor/ai-tutor-rebuild-panel";
import { InstructorDashboard } from "@/components/instructor/instructor-dashboard";
import { InstructorFigures } from "@/components/instructor/instructor-figures";
import { JoinRequestsCounter } from "@/components/admin/join-requests-counter";
import { useAuthStore } from "@/stores/authStore";
import {
  fetchCourseEnrollments,
  fetchInstructorDashboardSummary,
  fetchInstructorSubmissions,
  fetchStaffCourses,
} from "@/services/api/instructor";

export function TenantInstructorDashboardPage() {
  const { tenantSlug = "" } = useParams<{ tenantSlug: string }>();
  const { tenant: schoolTenant } = useSchoolBootstrap();
  const t = useTranslations("instructor");
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const canTeach = isAuthenticated && isStaffRole(user?.role);

  const dashboardQuery = useQuery({
    queryKey: ["instructor-dashboard", tenantSlug],
    queryFn: async () => {
      const courses = await fetchStaffCourses();
      const submissions = await fetchInstructorSubmissions();
      const initialCourse = courses[0] ?? null;
      const enrollments = initialCourse ? await fetchCourseEnrollments(initialCourse.id) : [];
      return { courses, submissions, enrollments, initialCourseId: initialCourse?.id ?? null };
    },
    enabled: canTeach,
  });

  // Counted server-side: the submissions list is capped and enrolments load one course at a time.
  const summaryQuery = useQuery({
    queryKey: ["instructor-dashboard-summary", tenantSlug],
    queryFn: fetchInstructorDashboardSummary,
    enabled: canTeach,
  });

  if (!isAuthenticated) {
    return <Navigate to={`/t/${tenantSlug}/login`} replace />;
  }

  if (!isStaffRole(user?.role)) {
    return (
      <main className="mx-auto max-w-lg px-4 py-16" data-testid="instructor-staff-only">
        <h1 className="font-serif text-2xl font-semibold text-[var(--brand-heading)]">
          {t("staffOnlyTitle")}
        </h1>
        <p className="mt-3 text-[var(--brand-muted)]">{t("staffOnlyBody")}</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to={`/t/${tenantSlug}/login`}
            className={buttonClasses({ variant: "accent", size: "sm" })}
            data-testid="instructor-staff-only-login"
          >
            {t("staffOnlyLogin")}
          </Link>
          <Link
            to={`/t/${tenantSlug}/dashboard`}
            className={buttonClasses({ variant: "secondary", size: "sm" })}
            data-testid="instructor-staff-only-dashboard"
          >
            {t("backToLearning")}
          </Link>
        </div>
      </main>
    );
  }

  if (dashboardQuery.isLoading) {
    return (
      <PageShell width="wide" data-testid="instructor-dashboard-loading">
        <p className="text-sm text-[var(--text-muted)]">{t("loading")}</p>
      </PageShell>
    );
  }

  if (dashboardQuery.isError || !dashboardQuery.data) {
    return (
      <PageShell width="wide" data-testid="instructor-dashboard-error">
        <p className="text-sm text-[var(--text-muted)]">{t("dashboardLoadFailed")}</p>
      </PageShell>
    );
  }

  const { courses, submissions, enrollments, initialCourseId } = dashboardQuery.data;

  return (
    <PageShell width="wide" data-testid="instructor-dashboard">
      <h1 className="text-2xl font-semibold">{t("dashboardTitle")}</h1>
      <p className="mt-2 text-[var(--text-muted)]">
        {t("welcome", { name: user?.name ?? user?.email ?? "" })}
      </p>
      <div className="mt-6">
        <InstructorFigures
          publishedCourses={courses.filter((course) => course.status === "published").length}
          summaryState={
            summaryQuery.data
              ? { status: "ready", summary: summaryQuery.data }
              : summaryQuery.isError
                ? { status: "error" }
                : { status: "loading" }
          }
        />
      </div>
      {user?.role === "tenant_admin" ? (
        <div className="mt-6">
          <JoinRequestsCounter tenantSlug={tenantSlug} />
        </div>
      ) : null}
      <div className="mt-6">
        <InstructorDashboard
          tenantSlug={tenantSlug}
          courses={courses.map((course) => ({
            id: course.id,
            slug: course.slug,
            title: course.title,
            status: course.status,
          }))}
          initialSubmissions={submissions}
          initialEnrollments={enrollments}
          initialCourseId={initialCourseId}
        />
      </div>
      {schoolTenant?.aiTutorEnabled === true ? (
        <div className="mt-6">
          <AiTutorRebuildPanel
            courses={courses.map((course) => ({ id: course.id, title: course.title }))}
          />
        </div>
      ) : null}
    </PageShell>
  );
}
